// SEQ-169 hire comments: shared model for audit-note comments that hang off any
// claims entity. Variations and hire register items both use it today; the V2
// ledger will attach to the same two tables without another migration.
//
// Two separate signals are layered on top of these types and must not be
// conflated:
//   Unread count  is per viewer, derived from claims_entity_views.last_read_at
//                 against each comment's created_at, and renders as a red bubble.
//   Needs review  is shared across the organization, denormalised from the most
//                 recent comment onto the entity itself (claims_variations
//                 .audit_flag, claims_hire_register.audit_flag), and renders as
//                 a coloured dot.

/** Audit flag shared by comments and by the denormalised flag on the entity. */
export type CommentFlag = "green" | "red" | "purple";

/** Entity kinds that can carry audit notes. Mirrors the CHECK constraint. */
export type CommentEntityType = "variation" | "hire_item";

export const ENTITY_COMMENTS_TABLE = "claims_entity_comments";
export const ENTITY_VIEWS_TABLE = "claims_entity_views";

/** Composite key of the uniqueness constraint on claims_entity_views. */
export const ENTITY_VIEW_CONFLICT_TARGET = "entity_type, entity_id, user_id";

export const COMMENT_FLAGS: readonly CommentFlag[] = ["green", "red", "purple"];
export const DEFAULT_COMMENT_FLAG: CommentFlag = "green";

/**
 * Table holding the denormalised needs-review flag for each entity kind. The
 * column is audit_flag on both.
 */
export const ENTITY_FLAG_TABLE = {
  variation: "claims_variations",
  hire_item: "claims_hire_register",
} as const satisfies Record<CommentEntityType, string>;

/** Minimum comment shape needed to compute unread counts. */
export interface CommentMeta {
  entity_type: CommentEntityType;
  entity_id: string;
  user_id: string;
  created_at: string | null;
}

/** Minimum read-receipt shape needed to compute unread counts. */
export interface ViewMeta {
  entity_type: CommentEntityType;
  entity_id: string;
  last_read_at: string;
}

/** Row shape as it comes back from the database, before the type is narrowed. */
export interface RawCommentMeta {
  entity_type: string;
  entity_id: string;
  user_id: string;
  created_at: string | null;
}

/** Row shape as it comes back from the database, before the type is narrowed. */
export interface RawViewMeta {
  entity_type: string;
  entity_id: string;
  last_read_at: string;
}

export interface UnreadCountInput {
  entityType: CommentEntityType;
  entityIds: string[];
  comments: CommentMeta[];
  /** View rows belonging to the current viewer only. */
  views: ViewMeta[];
  currentUserId: string;
}

export function isCommentFlag(value: unknown): value is CommentFlag {
  return typeof value === "string" &&
    (COMMENT_FLAGS as readonly string[]).includes(value);
}

/** Coerce a stored flag to a known value, defaulting to the audit_flag default. */
export function normaliseFlag(value: string | null | undefined): CommentFlag {
  return isCommentFlag(value) ? value : DEFAULT_COMMENT_FLAG;
}

export interface FlagAppearance {
  /** Fill colour of the status dot. */
  dot: string;
  /** Box shadow that gives the dot its halo. */
  glow: string;
  /** Tooltip text. */
  label: string;
}

// Only red has an exact token: --color-error is #ef4444. The repo has no token
// for #22c55e (--color-success is #10b981, a different green) and none for
// #9333ea at all, so those two stay literal rather than being approximated onto
// a near token, which would be a silent colour shift.
const FLAG_APPEARANCE: Record<CommentFlag, FlagAppearance> = {
  green: {
    dot: "#22c55e",
    glow: "0 0 6px rgba(34,197,94,0.4)",
    label: "All Good",
  },
  red: {
    dot: "var(--color-error)",
    glow: "0 0 6px rgba(239,68,68,0.4)",
    label: "Needs Review",
  },
  purple: {
    dot: "#9333ea",
    glow: "0 0 6px rgba(147,51,234,0.4)",
    label: "Agreement Reached",
  },
};

/** Dot colour, halo and tooltip for a stored flag value. */
export function flagAppearance(value: string | null | undefined): FlagAppearance {
  return FLAG_APPEARANCE[normaliseFlag(value)];
}

/**
 * Unread comments per entity for one viewer.
 *
 * Two behaviours are load bearing and predate this module:
 *   1. Your own comments never count as unread.
 *   2. No view row means everything is unread, because the read time falls back
 *      to epoch zero rather than to now.
 *
 * Entities with nothing unread are omitted from the result, so the badge can
 * treat a missing key as zero.
 */
export function computeUnreadCounts(input: UnreadCountInput): Record<string, number> {
  const { entityType, entityIds, comments, views, currentUserId } = input;
  const counts: Record<string, number> = {};

  const readTimes = new Map<string, number>();
  for (const view of views) {
    if (view.entity_type !== entityType) continue;
    readTimes.set(view.entity_id, new Date(view.last_read_at).getTime());
  }

  for (const entityId of entityIds) {
    const lastReadTime = readTimes.get(entityId) ?? 0;

    const unreadCount = comments.filter((comment) => {
      if (comment.entity_type !== entityType) return false;
      if (comment.entity_id !== entityId) return false;
      const isOtherUser = comment.user_id !== currentUserId;
      const isNewer = new Date(comment.created_at || 0).getTime() > lastReadTime;
      return isOtherUser && isNewer;
    }).length;

    if (unreadCount > 0) {
      counts[entityId] = unreadCount;
    }
  }

  return counts;
}

/** Narrow a stored entity_type, returning null for a value we do not render. */
export function toCommentEntityType(value: string): CommentEntityType | null {
  return value === "variation" || value === "hire_item" ? value : null;
}

/** Drop rows whose entity_type this build does not understand. */
export function toCommentMeta(
  rows: RawCommentMeta[] | null | undefined,
): CommentMeta[] {
  const result: CommentMeta[] = [];
  for (const row of rows || []) {
    const entityType = toCommentEntityType(row.entity_type);
    if (!entityType) continue;
    result.push({
      entity_type: entityType,
      entity_id: row.entity_id,
      user_id: row.user_id,
      created_at: row.created_at,
    });
  }
  return result;
}

/** Drop rows whose entity_type this build does not understand. */
export function toViewMeta(rows: RawViewMeta[] | null | undefined): ViewMeta[] {
  const result: ViewMeta[] = [];
  for (const row of rows || []) {
    const entityType = toCommentEntityType(row.entity_type);
    if (!entityType) continue;
    result.push({
      entity_type: entityType,
      entity_id: row.entity_id,
      last_read_at: row.last_read_at,
    });
  }
  return result;
}
